import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Anchor, Bot, Radio } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1758448721134-1798533ae917?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBuaWdodHxlbnwwfHx8fDE3Nzc4NDkxNDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Foundry() {
    const { search } = useLocation();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/foundry/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    const remaining = stats?.remaining ?? "—";
    const capacity = stats?.first_light_capacity ?? 10;

    return (
        <div data-testid="foundry-page">
            {/* Hero */}
            <section className="relative grain overflow-hidden">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url(${HERO_IMG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        opacity: 0.28,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/75 to-[#0A0A0A]" />
                <div className="relative mx-auto flex min-h-[78vh] max-w-[1400px] flex-col justify-center px-6 py-24 md:px-10">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="caption mb-6 inline-flex items-center gap-2" style={{ color: "var(--brand-accent)" }}>
                            <span className="h-px w-8" style={{ background: "var(--brand-accent)" }} />
                            The Foundry · Digital Construction Hub
                        </div>
                        <h1 className="font-serif text-6xl tracking-display sm:text-7xl lg:text-8xl text-[#FAFAFA] leading-[0.95]">
                            From <em className="italic" style={{ color: "var(--brand-accent)" }}>Blueprint</em>
                            <br />to Floating Vessel.
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-[#A3A3A3]">
                            Most agencies sell websites. We commission Sovereign Hulls — armored
                            recruitment platforms with a Ghost Crew, a pre-wired revenue model,
                            and a private subdomain in your name.
                        </p>

                        {/* Capacity ticker */}
                        <div
                            data-testid="capacity-ticker"
                            className="mt-10 inline-flex items-center gap-4 rounded-full border px-5 py-2"
                            style={{
                                borderColor: "rgba(201,169,110,0.4)",
                                background: "rgba(201,169,110,0.10)",
                            }}
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: "var(--brand-accent)" }} />
                                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: "var(--brand-accent)" }} />
                            </span>
                            <span className="caption" style={{ color: "var(--brand-accent)" }}>
                                First Light Wave
                            </span>
                            <span className="font-serif text-xl text-[#FAFAFA]">
                                {remaining}/{capacity} hulls remaining
                            </span>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to={{ pathname: "/foundry/interview", search }}
                                data-testid="cta-begin-interview"
                                className="inline-flex items-center justify-center gap-2 rounded-sm px-8 py-4 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.4)]"
                                style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                            >
                                Begin the Director Interview <ArrowRight size={16} />
                            </Link>
                            <Link
                                to={{ pathname: "/jobs", search }}
                                className="inline-flex items-center justify-center rounded-sm border border-white/10 px-8 py-4 text-sm font-medium text-[#FAFAFA] transition-all duration-300 hover:border-white/30 hover:bg-white/5"
                            >
                                Browse the Jobs Feed
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Three Pillars */}
            <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-10">
                <div className="mb-12 max-w-2xl">
                    <div className="caption text-[var(--brand-accent)]">Three Core Layers</div>
                    <h2 className="mt-4 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                        Hull. Crew. Signal.
                    </h2>
                </div>
                <div className="grid gap-px bg-white/5 sm:grid-cols-3">
                    {[
                        { Icon: Anchor, title: "The Hull", body: "Enterprise-grade codebase, armored for security and performance. Your Sovereign instance is white-labeled at your own subdomain — colors, logo, brand voice, all yours." },
                        { Icon: Bot, title: "The Crew", body: "A Ghost Crew of AI agents handles the boring labor — candidate scoring, lead capture, pipeline reminders. You run the show; the crew runs the ops." },
                        { Icon: Radio, title: "The Signal", body: "Pre-wired revenue model. Every hull ships with a Foundry Ledger, automated 60/30/10 LAW split tracking, and a connection to the Master Command Bridge." },
                    ].map(({ Icon, title, body }) => (
                        <div key={title} className="bg-[#0A0A0A] p-10">
                            <Icon size={28} style={{ color: "var(--brand-accent)" }} />
                            <h3 className="mt-6 font-serif text-2xl tracking-display text-[#FAFAFA]">{title}</h3>
                            <p className="mt-3 text-[#A3A3A3] leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section className="mx-auto max-w-[1200px] px-6 pb-24 md:px-10">
                <div className="caption text-[var(--brand-accent)]">How It Works</div>
                <h2 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                    From recruit to launch in three moves.
                </h2>
                <ol className="mt-12 space-y-8">
                    {[
                        { n: "01", title: "The Interview", body: "A recruit (Privateer) completes the Director Interview with the Wayfinder. Tell us your niche, experience, and the subdomain you want." },
                        { n: "02", title: "The Commission", body: "Pay the $250 First Light boarding fee via PayPal. Funds are tracked through the Foundry Ledger under the 60/30/10 LAW (60% recruiter / 30% platform / 10% CyberKids anchor)." },
                        { n: "03", title: "The Launch", body: "Your Sovereign Hull is provisioned by the Ghost Crew. You begin submitting candidates and earning placement bounties of $10k–$25k." },
                    ].map(({ n, title, body }) => (
                        <li key={n} className="flex gap-8 border-t border-white/5 pt-8">
                            <span className="font-mono text-sm text-[#737373]">{n}</span>
                            <div>
                                <h3 className="font-serif text-2xl tracking-display text-[#FAFAFA]">{title}</h3>
                                <p className="mt-2 max-w-2xl text-[#A3A3A3] leading-relaxed">{body}</p>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className="mt-12">
                    <Link
                        to={{ pathname: "/foundry/interview", search }}
                        className="inline-flex items-center justify-center gap-2 rounded-sm px-8 py-4 text-sm font-medium transition-all duration-300"
                        style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    >
                        Begin the Director Interview <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
